import { inject, Injectable, Input } from '@angular/core'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { Duration, Instant } from '@js-joda/core'
import { BehaviorSubject, combineLatest, interval, Observable } from 'rxjs'
import { filter, map, switchMap } from 'rxjs/operators'
import { RouterQueryParams } from '../../../shared/router-query-params.service'
import { MexusViewMode } from '../view-mode-context/mexus-view-mode'
import { ViewModeContextService } from '../view-mode-context/view-mode-context.service'

@Injectable({
  providedIn: 'root',
})
export class LiveTimestampService {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)
  private readonly _timestamp$ = new BehaviorSubject<Instant>(Instant.now())
  private readonly _autoRefreshFrequency$ = new BehaviorSubject<Duration>(Duration.ofMinutes(5))
  private readonly _autoRefreshTimer$: Observable<number>
  private routerQueryParams = inject(RouterQueryParams)
  private viewModeService = inject(ViewModeContextService)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    // Check for timestamp and refresh query params on all router levels
    this.routerQueryParams.params
      .pipe(map((params) => [params['timestamp'], params['refresh']]))
      .subscribe({
        next: ([timestampStr, refreshStr]) => {
          this.updateTimestampFromQuery(timestampStr)
          this.updateRefreshFrequencyFromQuery(refreshStr)
        },
        error: (err) =>
          this.log.error('Failed to set timestamp and refresh frequency from query params!', err),
      })

    // Set up refresh timer
    this._autoRefreshTimer$ = this._autoRefreshFrequency$.pipe(
      filter((frequency) => frequency != null),
      switchMap((frequency) => interval(frequency.toMillis()))
    )

    // Periodically update timestamp
    combineLatest([this._autoRefreshTimer$, this.viewModeService.viewMode$])
      .pipe(
        filter(([_, viewMode]) => this.canUpdateTimestamp(this.autoRefreshFrequency, viewMode)),
        map(() => Instant.now())
      )
      .subscribe((timestamp) => (this.timestamp = timestamp))
  }

  /***************************************************************************
   *                                                                         *
   * Company Getters / Setters                                               *
   *                                                                         *
   **************************************************************************/

  public get timestamp$(): Observable<Instant> {
    return this._timestamp$.pipe(filter((timestamp) => !!timestamp))
  }

  public get timestamp(): Instant {
    return this._timestamp$.getValue()
  }

  public set timestamp(timestamp: Instant) {
    this._timestamp$.next(timestamp)
  }

  public get autoRefreshFrequency(): Duration {
    return this._autoRefreshFrequency$.getValue()
  }

  @Input()
  public set autoRefreshFrequency(frequency: Duration) {
    this._autoRefreshFrequency$.next(frequency)
  }

  /***************************************************************************
   *                                                                         *
   * Private methods                                                         *
   *                                                                         *
   **************************************************************************/

  private canUpdateTimestamp(frequency: Duration | null, viewMode: MexusViewMode): boolean {
    return (
      frequency != null &&
      frequency.toMillis() > Duration.ZERO.toMillis() &&
      viewMode === MexusViewMode.LIVE
    )
  }

  private updateTimestampFromQuery(timestampStr: string | null): void {
    const timestamp = timestampStr ? Instant.parse(timestampStr) : null
    if (timestamp) {
      this.timestamp = timestamp
    }
  }

  private updateRefreshFrequencyFromQuery(refreshStr: string | null): void {
    const refresh = refreshStr ? Duration.parse(refreshStr) : null
    if (refresh) {
      this.autoRefreshFrequency = refresh
    }
  }
}
